import React, { ReactNode, useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { formatDateDisplay, formatDatePath } from '../utils';
import { useAuth } from '../contexts/AuthContext';
import { useFeatureFlag } from '../contexts/FeatureFlagContext';
import { useSiteName, useSiteTagline } from '../contexts/SiteConfigContext';
import FeatureFlag from '../components/FeatureFlag';
import { AuthenticatedUpcomingTips } from '../components/upcoming';
import { AnalysisTypeToggleCompact } from '../components/common/AnalysisTypeToggle';
import { useSubscriptionAccess } from '../hooks/useSubscriptionAccess';
import DailyTipsButton from '../components/DailyTipsButton';
import { NewLookBanner, UiVersionSwitch } from '@shared/ui/uiVersion';

interface MainLayoutProps {
  children: ReactNode;
}

export const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isLoading, user, logout } = useAuth();
  const siteName = useSiteName();
  const siteTagline = useSiteTagline();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const { activeSubscriptions, isLoading: subscriptionsLoading } = useSubscriptionAccess();
  
  // Handle clicks outside the mobile menu to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target as Node)) {
        setMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  // Log authentication state and check for unconfirmed users
  useEffect(() => {
    console.log('MainLayout: isAuthenticated =', isAuthenticated, 'isLoading =', isLoading);
    
    // Check if we have tempPhoneNumber and tempUserId in localStorage
    // This indicates an unconfirmed user that needs to verify their phone
    const tempPhoneNumber = localStorage.getItem('tempPhoneNumber');
    const tempUserId = localStorage.getItem('tempUserId');
    
    if (!isAuthenticated && !isLoading && tempPhoneNumber && tempUserId) {
      // If we're not on the verify-phone page already, redirect there
      if (!location.pathname.includes('/verify-phone')) {
        console.log('Detected unconfirmed user, redirecting to verification page');
        navigate('/verify-phone');
      }
    }
  }, [isAuthenticated, isLoading, navigate, location.pathname]);
  
  // Extract date from URL or use today
  const getDateFromUrl = (): Date => {
    // Check if on Daily Tips page with query parameter
    if (location.pathname === '/daily-tips') {
      const searchParams = new URLSearchParams(location.search);
      const dateParam = searchParams.get('date');

      if (dateParam && /^\d{8}$/.test(dateParam)) {
        const year = parseInt(dateParam.substring(0, 4));
        const month = parseInt(dateParam.substring(4, 6)) - 1;
        const day = parseInt(dateParam.substring(6, 8));
        return new Date(year, month, day);
      }
      return new Date();
    }

    // Standard path-based date extraction
    const pathParts = location.pathname.split('/');
    const dateString = pathParts[1];

    if (dateString && /^\d{8}$/.test(dateString)) {
      const year = parseInt(dateString.substring(0, 4));
      const month = parseInt(dateString.substring(4, 6)) - 1;
      const day = parseInt(dateString.substring(6, 8));
      return new Date(year, month, day);
    }

    return new Date();
  };

  const [selectedDate, setSelectedDate] = useState<Date>(getDateFromUrl());

  // Update date when URL changes
  useEffect(() => {
    setSelectedDate(getDateFromUrl());
  }, [location.pathname, location.search]);
  
  // Navigation handlers
  const goToPreviousDay = () => {
    const prevDay = new Date(selectedDate);
    prevDay.setDate(prevDay.getDate() - 1);
    setSelectedDate(prevDay);

    // Use query params for Daily Tips page, path params for others
    if (location.pathname === '/daily-tips') {
      navigate(`/daily-tips?date=${formatDatePath(prevDay)}`);
    } else {
      navigate(`/${formatDatePath(prevDay)}`);
    }
  };

  const goToToday = () => {
    const today = new Date();
    setSelectedDate(today);

    // Use query params for Daily Tips page, path params for others
    if (location.pathname === '/daily-tips') {
      navigate('/daily-tips');
    } else {
      navigate(`/${formatDatePath(today)}`);
    }
  };

  const goToNextDay = () => {
    const nextDay = new Date(selectedDate);
    nextDay.setDate(nextDay.getDate() + 1);
    setSelectedDate(nextDay);

    // Use query params for Daily Tips page, path params for others
    if (location.pathname === '/daily-tips') {
      navigate(`/daily-tips?date=${formatDatePath(nextDay)}`);
    } else {
      navigate(`/${formatDatePath(nextDay)}`);
    }
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newDate = new Date(e.target.value);
    setSelectedDate(newDate);

    // Use query params for Daily Tips page, path params for others
    if (location.pathname === '/daily-tips') {
      navigate(`/daily-tips?date=${formatDatePath(newDate)}`);
    } else {
      navigate(`/${formatDatePath(newDate)}`);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-racing-gray">
      {/* Header */}
      <header className="site-header">
        <div className="flex justify-between items-center w-full">
          <Link to="/" className="flex flex-col items-start md:flex-row md:items-center">
            <div className="flex items-center">
              <img 
                src="/tippingiq_transparent.svg" 
                alt="TippingIQ"
                className="h-10 md:h-10 w-auto"
              />
            </div>
            <span className={isAuthenticated ? "brand-tagline md:ml-4" : "mobile-tagline md:ml-4"}>
              {siteTagline}
            </span>
          </Link>

          {/* Desktop Auth Buttons - Only shown when authenticated */}
          {isAuthenticated && (
            <div className="auth-buttons hidden md:block">
              {isLoading ? (
                <span className="text-sm text-gray-500">Loading...</span>
              ) : (
                <div className="flex items-center space-x-4">
                  <span className="text-sm text-gray-700">
                    Welcome, {user?.firstName || 'User'}
                  </span>
                  <div className="flex space-x-2">
                    <Link
                      to="/high-probability"
                      className="text-sm inline-flex items-center gap-2 px-4 py-2 rounded font-medium transition-colors"
                      style={{ backgroundColor: '#111827', color: 'white', border: '1px solid #374151' }}
                      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#1f2937'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#111827'; }}
                    >
                      <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#22c55e', boxShadow: '0 0 8px #22c55e', flexShrink: 0 }} />
                      Tippa's Picks
                    </Link>
                    <UiVersionSwitch className="text-sm bg-amber-400 text-gray-900 font-semibold px-4 py-2 rounded hover:bg-amber-300" />
                    <Link
                      to="/profile"
                      className="text-sm bg-deep-navy text-white px-4 py-2 rounded hover:bg-deep-navy-dark"
                    >
                      Account
                    </Link>
                    <DailyTipsButton className="text-sm" />
                    <button
                      onClick={() => logout()}
                      className="text-sm bg-bookie-red text-white px-4 py-2 rounded hover:bg-bookie-red-dark"
                    >
                      Logout
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Mobile Hamburger Menu - Only shown when authenticated */}
          {isAuthenticated && (
            <>
              <div className="md:hidden relative">
                <button 
                  className={`hamburger-menu-button ${mobileMenuOpen ? 'open' : ''}`} 
                  onClick={toggleMobileMenu}
                  aria-label="Toggle navigation menu"
                >
                  <span></span>
                  <span></span>
                  <span></span>
                </button>
              </div>
              
              {/* Mobile Menu Dropdown - Rendered outside the button container */}
              {mobileMenuOpen && (
                <div className="mobile-menu" ref={mobileMenuRef}>
                  {isLoading ? (
                    <span className="text-sm text-gray-500 p-4">Loading...</span>
                  ) : (
                    <div className="mobile-menu-items">
                      <div className="mobile-menu-header">
                        <span className="text-sm font-bold">
                          Welcome, {user?.firstName || 'User'}
                        </span>
                      </div>
                      <Link
                        to="/high-probability"
                        className="mobile-menu-item font-medium inline-flex items-center gap-2"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#22c55e', boxShadow: '0 0 8px #22c55e', flexShrink: 0 }} />
                        Tippa's Picks
                      </Link>
                      <UiVersionSwitch className="mobile-menu-item font-semibold" />
                      <Link
                        to="/profile"
                        className="mobile-menu-item"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        Account
                      </Link>
                      <Link
                        to="/pricing"
                        className="mobile-menu-item"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        Pricing
                      </Link>
                      <Link
                        to="/daily-tips"
                        className="mobile-menu-item"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        Daily Tips
                      </Link>
                      <button
                        onClick={() => {
                          logout();
                          setMobileMenuOpen(false);
                        }}
                        className="mobile-menu-item text-bookie-red"
                      >
                        Logout
                      </button>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </header>

      <NewLookBanner />

      {/* Date Navigation or CTA Bar based on authentication status */}
      {isAuthenticated ? (
        // Date Navigation for authenticated users
        <div className="date-navigation">
          <div className="flex flex-col sm:flex-row justify-between items-center w-full">
            <h2 className="date-display">{formatDateDisplay(selectedDate)}</h2>

            <div className="flex items-center space-x-4">
              {/* Analysis Type Toggle */}
              <AnalysisTypeToggleCompact className="hidden sm:flex" />
              
              <div className="date-controls">
                <button
                    className="nav-button"
                    onClick={goToPreviousDay}
                >
                  <span className="arrow">←</span> <span className="button-text">Previous</span>
                </button>

                <button
                    className="nav-button today"
                    onClick={goToToday}
                >
                  Today
                </button>

                <button
                    className="nav-button"
                    onClick={goToNextDay}
                >
                  <span className="button-text">Next</span> <span className="arrow">→</span>
                </button>

                <input
                    type="date"
                    value={selectedDate.toISOString().split('T')[0]}
                    onChange={handleDateChange}
                    className="ml-2 p-1 rounded border border-racing-gray-darker text-deep-navy bg-white"
                />
              </div>
            </div>
          </div>
          
          {/* Mobile Analysis Type Toggle */}
          <div className="sm:hidden mt-2 flex justify-center">
            <AnalysisTypeToggleCompact />
          </div>
        </div>
      ) : (
        // CTA Bar for non-authenticated users
        <div className="date-navigation">
          <div className="flex flex-col justify-center items-center w-full px-4 space-y-3 sm:space-y-0 sm:flex-row sm:justify-between">
            <span className="cta-message text-center sm:text-left">
              Create an account to access daily racing tips
            </span>
            <div className="flex flex-col space-y-2 w-full sm:w-auto sm:flex-row sm:space-y-0 sm:space-x-2">
              {/* Primary actions - more prominent on mobile */}
              <div className="flex space-x-2">
                <Link
                  to="/register"
                  className="flex-1 sm:flex-none bg-race-gold text-deep-navy px-4 py-2 rounded font-medium hover:bg-race-gold-dark transition-colors text-center"
                >
                  Join Now
                </Link>
                <Link
                  to="/login"
                  className="flex-1 sm:flex-none bg-deep-navy text-white px-4 py-2 rounded hover:bg-deep-navy-dark transition-colors text-center"
                >
                  Login
                </Link>
              </div>
              {/* Secondary actions */}
              <div className="flex space-x-2">
                <Link
                  to="/preview"
                  className="flex-1 sm:flex-none bg-racing-green text-white px-4 py-2 rounded hover:bg-racing-green-dark transition-colors text-center"
                >
                  Preview
                </Link>
                <Link
                  to="/pricing"
                  className="flex-1 sm:flex-none bg-racing-green text-white px-4 py-2 rounded hover:bg-racing-green-dark transition-colors text-center"
                >
                  Pricing
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Promotional Bar - Shown for unauthenticated users or authenticated users without paid subscriptions (excluding default package) */}
      {(() => {
        // Always show for unauthenticated users
        if (!isAuthenticated) return true;
        
        // For authenticated users, wait for subscription data to load
        if (subscriptionsLoading) return false;
        
        // Debug logging to understand subscription data
        console.log('MainLayout: activeSubscriptions =', activeSubscriptions);
        
        // Check if user has any paid subscriptions (excluding default package)
        // Since activeSubscriptions should already only include active ones, we just need to filter out default packages
        const paidSubscriptions = activeSubscriptions.filter(sub =>
          sub.package.packageCode !== 'DEFAULT' &&
          sub.package.name !== 'Default Package'
        );
        
        console.log('MainLayout: paidSubscriptions =', paidSubscriptions);
        console.log('MainLayout: should show banner =', paidSubscriptions.length === 0);
        
        // Show banner if no paid subscriptions (only default package or no subscriptions at all)
        return paidSubscriptions.length === 0;
      })() && (
        /* FIRSTWEEK50 Banner - Temporarily hidden as requested */
        null
        /* Original banner code commented out for easy restoration:
        <div className="bg-gradient-to-r from-racing-green to-racing-green-dark text-white py-3 border-b border-racing-green-dark">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-center">
              <div className="flex items-center space-x-2">
                <span className="text-lg font-bold">🎯</span>
                <span className="font-medium">
                  Get 50% off your first package with code{' '}
                  <span className="font-bold bg-white text-racing-green px-2 py-1 rounded text-sm">
                    FIRSTWEEK50
                  </span>
                </span>
              </div>
            </div>
          </div>
        </div>
        */
      )}

      {/* Upcoming Tips - Only shown when authenticated and on appropriate pages */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        <AuthenticatedUpcomingTips />
      </div>

      {/* Main content */}
      <main className="flex-grow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {children}
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white shadow-sm-up">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-500">
              &copy; {new Date().getFullYear()} {siteName}. All rights reserved.
            </div>
            <div className="flex space-x-4">
              <Link to="/pricing" className="text-sm text-gray-500 hover:text-racing-green">
                Pricing
              </Link>
              <Link to="/terms" className="text-sm text-gray-500 hover:text-racing-green">
                Terms
              </Link>
              <Link to="/privacy" className="text-sm text-gray-500 hover:text-racing-green">
                Privacy
              </Link>
              <Link to="/contact" className="text-sm text-gray-500 hover:text-racing-green">
                Contact
              </Link>
            </div>
          </div>
        </div>
      </footer>
      
      {/* Gambling Legislation Disclaimer - Matching Header Bar Style */}
      <div className="legislation date-navigation">
        <div className="flex flex-col sm:flex-row justify-center items-center w-full px-4">
          <div className="text-center">
            <div className="legislation__tag-line font-semibold text-sm mb-1">
              WHAT'S GAMBLING REALLY COSTING YOU?
            </div>
            <div className="legislation__call-to-action text-xs">
              For free and confidential support call{' '}
              <a 
                href="tel:1800858858" 
                className="font-medium text-white hover:text-gray-300 underline"
              >
                1800 858 858
              </a>
              {' '}or visit{' '}
              <a 
                href="https://www.gamblinghelponline.org.au/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="font-medium text-white hover:text-gray-300 underline"
              >
                gamblinghelponline.org.au
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MainLayout;
